import { parsePhoneNumberWithError } from "libphonenumber-js";
import i18n from "../../plugins/I18nPlugin";

/**
 * Canonical email validation pattern shared across all email rules.
 * @see https://emailregex.com/
 */
export const EMAIL_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * @see https://emailregex.com/
 * @param message the message to display
 * @returns validates an email
 */
export function emailRule(message = "report.formPersonalData.validEmail"): (value: any) => boolean | string {
  return (value) => EMAIL_REGEX.test(value) || String(i18n.tc(message));
}

/**
 * @see https://emailregex.com/
 * @param message the message to display
 * @returns validates an email
 */
export function emailsRule(message = "report.formPersonalData.validEmail"): (value: string[]) => boolean | string {
  return (value) => value.every((v) => EMAIL_REGEX.test(v)) || String(i18n.tc(message));
}

export function disallowUmlaut(message = "report.formPersonalData.umlaut"): (value: any) => boolean | string {
  const regex = RegExp(/[äöüÄÖÜ]/);

  return (value) => !regex.test(value) || String(i18n.tc(message));
}

export function emailRuleOptional(message = "report.formPersonalData.validEmail"): (value: any) => boolean | string {
  return (value) => EMAIL_REGEX.test(value) || value == "" || value == null || String(i18n.tc(message));
}

export function emailRuleNullable(message = "report.formPersonalData.validEmail"): (value: any) => boolean | string {
  return (value) => EMAIL_REGEX.test(value) || value == null || String(i18n.tc(message));
}

/*
  Value shouldn't contain a single number.
*/
export function noNumberRule(message = "report.formPersonalData.validValue"): (value: any) => boolean | string {
  const regex = RegExp(/^([^0-9]*)$/);
  return (value) => regex.test(value) || String(i18n.tc(message));
}

/*
Value can only be a 5 digit number between 1000 and 99999
*/
export function germanZipRule(message = "report.formPersonalData.validZip"): (value: any) => boolean | string {
  const regex = RegExp(/^\d{4,5}$/);

  return (value) => (regex.test(value) && (value > 1000 || value < 99999)) || !value || String(i18n.tc(message));
}

/*
  Match a single character present in the list below [+]
  ? matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
  + matches the character + literally (case sensitive)
  Match a single character present in the list below [0-9\s]
  * matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
  0-9 matches a single character in the range between 0 (index 48) and 9 (index 57) (case sensitive)
  \s matches any whitespace character (equivalent to [\r\n\t\f\v ])
*/
export function phoneRule(message = "report.formPersonalData.validPhone"): (value: any) => boolean | string {
  const regex = RegExp(/^[+]?[0-9\s]*$/);
  return (value) => regex.test(value) || String(i18n.tc(message));
}

/*
  Match a single character present in the list below [+]
  ? matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
  + matches the character + literally (case sensitive)
  Match a single character present in the list below [0-9\s]
  * matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
  0-9 matches a single character in the range between 0 (index 48) and 9 (index 57) (case sensitive)
  \s matches any whitespace character (equivalent to [\r\n\t\f\v ])
  can be empty
*/
export function phoneRuleOptional(message = "report.formPersonalData.validPhone"): (value: any) => boolean | string {
  const regex = RegExp(/^[+]?[0-9\s]*$/);
  return (value) => regex.test(value) || value == "" || value == null || String(i18n.tc(message));
}

/*
  Match a single character present in the list below [+-]
  ? matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
  + matches the character + literally (case sensitive)
  Match a single character present in the list below [0-9\s]
  * matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
  0-9 matches a single character in the range between 0 (index 48) and 9 (index 57) (case sensitive)
  \s matches any whitespace character (equivalent to [\r\n\t\f\v ])

  Used to update the profile (- in phone numer valid)
*/
export function extendetPhoneRule(message = "report.formPersonalData.validPhone"): (value: any) => boolean | string {
  const regex = RegExp(/^[+-]?[0-9\s]*$/);
  return (value) => regex.test(value) || String(i18n.tc(message));
}

/**
 * Validates that a phone number is in valid international (E.164) format using libphonenumber-js.
 * Requires the input to start with '+' and parses without a default country to enforce international format.
 * @param message the i18n message key to display on validation failure
 * @returns a validation function compatible with Vuetify rules
 */
export function phoneInternationalRule(
  message = "report.formPersonalData.validPhoneInternational"
): (value: any) => boolean | string {
  return (value) => {
    if (!value) return true;

    if (!String(value).startsWith("+")) {
      return String(i18n.tc(message));
    }

    try {
      const parsedNumber = parsePhoneNumberWithError(value);
      return parsedNumber.isValid() || String(i18n.tc(message));
    } catch (error) {
      return String(i18n.tc(message));
    }
  };
}
